import { ActivityResponderDialog } from "@/components/conversations/activities/activity-responder-dialog";
import { AnswerPill } from "@/components/conversations/activities/hot-takes/answer-pill";
import { useResponderSend } from "@/components/conversations/activities/use-responder-send";
import { isHotTakesActivity, useActivity } from "@/lib/api/activities";
import { FireIcon } from "@heroicons/react/24/outline";
import type { HotTakeAnswer } from "@somethings-ui/core/hot-takes";
import { useCallback, useMemo, useState } from "react";

interface HotTakesResponderProps {
  isOpen: boolean;
  entityId: string;
  teenName: string;
  onClose: () => void;
}

export function HotTakesResponder({
  isOpen,
  entityId,
  teenName,
  onClose,
}: HotTakesResponderProps) {
  const { data: activity } = useActivity(entityId);
  const [answers, setAnswers] = useState<Record<string, HotTakeAnswer>>({});

  const statements = useMemo(
    () => (isHotTakesActivity(activity) ? activity.config.statements : []),
    [activity],
  );

  const canSend =
    statements.length > 0 &&
    statements.every((statement) => answers[statement.id]);

  const { isSending, handleSend } = useResponderSend({
    entityId,
    activityType: "hot_takes",
    isOpen,
    canSend,
    markInProgressOnOpen: true,
    buildResult: () => ({
      responses: statements.map((statement) => ({
        statementId: statement.id,
        responderAnswer: answers[statement.id],
      })),
    }),
    onClose,
  });

  const handleAnswer = useCallback(
    (statementId: string, answer: HotTakeAnswer) => {
      setAnswers((prev) => ({ ...prev, [statementId]: answer }));
    },
    [],
  );

  return (
    <ActivityResponderDialog
      isOpen={isOpen}
      onClose={onClose}
      icon={
        <FireIcon
          aria-hidden
          strokeWidth={2}
          className="h-6 w-6 shrink-0 text-text-primary"
        />
      }
      title="Hot takes"
      subtitle={`${teenName} sent you these hot takes. Answer to reveal their answers!`}
      contentClassName="w-[672px] max-w-[672px] sm:max-w-[672px]"
      canSend={canSend}
      isSending={isSending}
      onSend={handleSend}
    >
      <div className="flex flex-col gap-6">
        {statements.map((statement, i) => (
          <div
            key={statement.id}
            className="flex w-full items-center gap-3 py-1"
          >
            <span
              className="w-6 shrink-0 font-mono text-sm font-medium text-tertiary"
              aria-hidden
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-base leading-snug text-text-primary">
              {statement.text}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <AnswerPill
                label="Agree"
                selected={answers[statement.id] === "agree"}
                onClick={() => handleAnswer(statement.id, "agree")}
              />
              <AnswerPill
                label="Disagree"
                selected={answers[statement.id] === "disagree"}
                onClick={() => handleAnswer(statement.id, "disagree")}
              />
            </div>
          </div>
        ))}
      </div>
    </ActivityResponderDialog>
  );
}
